import type { Modifier, Placement, Rect } from '@popperjs/core'

// the arrow of the frontend kit is a 22px square turned by 45 degrees (its base on the edge is about 31px wide):
// keep its centre this far from the corners, so that it does not stick out of the popover
const ARROW_EDGE_DISTANCE = 16

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right'

// 'bottom-left' (a position of the props) or 'bottom-start' (a placement of Popper) are both at the bottom
export const getSide = (position: string | undefined): PopoverSide =>
    (position?.split('-')[0] as PopoverSide | undefined) ?? 'bottom'

// the frontend kit names the arrow after the edge of the popover it sits on, the opposite of the placement
const ARROW_CLASS: Record<PopoverSide, string> = {
    top: '-bottom-center',
    bottom: '-top-center',
    left: '-right-center',
    right: '-left-center',
}

export const getArrowClass = (placement: Placement | undefined): string => ARROW_CLASS[getSide(placement)]

/**
 * Distance of the arrow from the start of the popover edge it sits on (left for top / bottom, top for left / right),
 * so that it points at the centre of the target, also when the popover was moved to stay inside the screen.
 */
export const getArrowOffset = (
    placement: Placement,
    reference: Rect,
    popper: { x: number; y: number; width: number; height: number }
): number => {
    const side = getSide(placement)
    const horizontal = side === 'top' || side === 'bottom'
    const targetCentre = horizontal
        ? reference.x + reference.width / 2 - popper.x
        : reference.y + reference.height / 2 - popper.y
    const size = horizontal ? popper.width : popper.height

    return Math.min(Math.max(targetCentre, ARROW_EDGE_DISTANCE), size - ARROW_EDGE_DISTANCE)
}

// Popper modifier: hands the arrow offset to the stylesheet as `--popover-arrow-offset`
export const arrowOffsetModifier: Modifier<'arrowOffset', object> = {
    name: 'arrowOffset',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        const offsets = state.modifiersData.popperOffsets
        if (!offsets) return

        const offset = getArrowOffset(state.placement, state.rects.reference, {
            ...offsets,
            width: state.rects.popper.width,
            height: state.rects.popper.height,
        })
        state.styles.popper = {
            ...state.styles.popper,
            ['--popover-arrow-offset' as string]: `${offset}px`,
        }
    },
}
